import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "../ThemeToggle";
import { LanguageToggle } from "../LanguageToggle";
import { ChLogo } from "../ui/ChLogo";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const HamburgerIcon = ({ isOpen }: { isOpen: boolean }) => (
  <div className="w-5 h-3.5 flex flex-col justify-between">
    <motion.span
      animate={isOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
      transition={{ duration: 0.25 }}
      className="block h-[1.5px] w-full origin-center bg-current"
    />
    <motion.span
      animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="block h-[1.5px] w-full bg-current"
    />
    <motion.span
      animate={isOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
      transition={{ duration: 0.25 }}
      className="block h-[1.5px] w-full origin-center bg-current"
    />
  </div>
);

export const Navbar = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === "dark";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  /** Dark hero and dark tiles — use light chrome while overlapping them. */
  const [overDarkSurface, setOverDarkSurface] = useState(isDark);

  const navLinks = [
    { name: t("nav.about"), href: "#about" },
    { name: t("nav.projects"), href: "#projects" },
    { name: t("nav.certifications"), href: "#certifications" },
    { name: t("nav.contact"), href: "#contact" },
  ];

  const lightChrome = isDark || overDarkSurface;
  const ink = lightChrome ? "#f5f5f5" : "#171717";
  const muted = lightChrome ? "rgba(245,245,245,0.72)" : "#737373";

  useEffect(() => {
    const sections = navLinks.map((l) => l.href);
    const darkSurfaceIds = isDark
      ? ["#about", "#experience", "#projects", "#certifications", "#contact"]
      : ["#footer"];

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const overHero = isDark && scrollY < window.innerHeight * 0.72;
      const navLine = 76;
      const overDarkTile = darkSurfaceIds.some((id) => {
        const el = document.querySelector(id);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top < navLine && r.bottom > navLine;
      });
      setOverDarkSurface(overHero || overDarkTile);

      let current: number | null = null;
      const viewportMid = window.innerHeight / 2;

      if (
        window.innerHeight + Math.round(scrollY) >=
        document.documentElement.scrollHeight - 50
      ) {
        current = sections.length - 1;
      } else {
        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.querySelector(sections[i]!);
          if (el && el.getBoundingClientRect().top <= viewportMid) {
            current = i;
            break;
          }
        }
      }
      if (scrollY < 80) current = null;
      setActiveIndex(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [t, isDark]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 88,
        behavior: "smooth",
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className="fixed top-0 inset-x-0 z-50 flex h-[calc(3.5rem+env(safe-area-inset-top)+var(--site-frame))] items-end justify-between bg-transparent pb-1 sm:h-[calc(4rem+var(--site-frame))] sm:items-center sm:pb-0 px-5 sm:px-6 md:px-8"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + var(--site-frame))",
        }}
      >
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
            setIsMobileMenuOpen(false);
          }}
          className="flex items-center gap-2.5"
        >
          <ChLogo className="h-7 w-7" />
          <h1
            className="text-[18px] font-normal leading-none lg:text-[19px]"
            style={{ color: ink }}
          >
            {t("hero.name")}
          </h1>
        </a>

        <div className="flex items-center gap-6 lg:gap-8">
          <div className="hidden items-center gap-7 md:flex lg:gap-9">
            {navLinks.map((link, i) => {
              const active = activeIndex === i;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="relative text-[14px] lg:text-[15px] transition-colors duration-200"
                  style={{
                    color: active ? ink : muted,
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {link.name}
                  {active && (
                    <span
                      className="absolute -bottom-1 left-1/2 h-0.5 w-3.5 -translate-x-1/2 rounded-full"
                      style={{ background: ink }}
                    />
                  )}
                </a>
              );
            })}
          </div>

          <div className="flex items-center gap-0.5">
            <div
              className="flex items-center rounded-full p-0.5"
              style={{
                background: lightChrome
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.05)",
              }}
            >
              <LanguageToggle forceLight={lightChrome} />
              <ThemeToggle forceLight={lightChrome} />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="flex md:hidden items-center justify-center w-10 h-10 transition-colors duration-300"
              style={{ color: ink }}
              aria-label="Toggle menu"
            >
              <HamburgerIcon isOpen={isMobileMenuOpen} />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{
                background: isDark ? "rgba(0,0,0,0.55)" : "rgba(29,29,31,0.28)",
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="fixed top-[calc(3.5rem+env(safe-area-inset-top)+var(--site-frame)+0.5rem)] inset-x-3 z-50 max-h-[min(70vh,28rem)] overflow-y-auto rounded-2xl sm:inset-x-4 sm:top-[calc(4rem+var(--site-frame))] md:hidden"
              style={{
                background: isDark ? "#171717" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                boxShadow: isDark
                  ? "0 16px 40px rgba(0,0,0,0.45)"
                  : "0 16px 40px rgba(0,0,0,0.12)",
              }}
            >
              <div className="p-2 flex flex-col">
                {navLinks.map((link, i) => {
                  const active = activeIndex === i;
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="px-4 py-3.5 text-[17px] rounded-xl transition-colors"
                      style={{
                        color: isDark ? "#f5f5f5" : "#171717",
                        background: active
                          ? isDark
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.05)"
                          : "transparent",
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {link.name}
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
